import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { isEmpty, reject } from 'lodash';
import { Card, Col, Collapse, Form, Input, Row, Typography } from 'antd';
import Text from 'antd/lib/typography/Text';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid';
import RouterLink from 'components/RouterLink';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  findByTpReferenceAndTpName,
  FindByTpReferenceAndTpNameParams,
  TpList,
} from 'services/thirdParty';
import { useHistory } from 'react-router-dom';
import ParListSelect from 'components/ParListSelect';
import ThirdPartyInput, {
  ThirdPartyForProposalType,
} from 'components/ThirdPartyInput';
import RoleTag from '../RoleTag';
import Title from 'components/Title';
import FormInput from 'components/FormInput';
import SearchButton from 'components/SearchButton';
import { useForm } from 'antd/lib/form/Form';

const { Panel } = Collapse;

export type ThirdPartySearchProps = {
  onSelect?: (item: TpList) => void;
  isProposalCreate?: boolean;
  setThirdPartyForProposal?: (
    ThirdPartyForProposal: ThirdPartyForProposalType
  ) => void;
  parcodeRole?: string;
};

const ThirdPartyListSFS = ({
  onSelect,
  parcodeRole,
  isProposalCreate,
}: ThirdPartySearchProps) => {
  const initialSearchParams: FindByTpReferenceAndTpNameParams = {
    parentid: 0,
    role: '',
    tpfiscalid: '',
    tpname: '',
    tpreference: '',
    tpcardid: '',
    tptype: '',
  };
  const [searchParams, setSearchParams] =
    useState<FindByTpReferenceAndTpNameParams>(initialSearchParams);

  const history = useHistory();

  const [form] = useForm();

  const [search, setSearch] = useState(false);

  const {
    data: thirdParties,
    refetch,
    isFetching,
  } = useQuery(
    ['findByTpReferenceAndTpName', searchParams],
    () =>
      findByTpReferenceAndTpName(searchParams).then((res) => res.list || []),
    { enabled: search }
  );

  const columns: DataGridColDef<TpList>[] = [
    {
      dataIndex: 'tpreference',
      render: (tpreference, record) =>
        onSelect ? (
          <Typography.Link onClick={() => onSelect(record)}>
            {tpreference}
          </Typography.Link>
        ) : (
          <RouterLink to={`/thirdparty-sfs/${record.tpid}/TP`}>
            {tpreference}
          </RouterLink>
        ),
      fixed: 'left',
      width: '10%',
      filtrable: true,
    },
    {
      dataIndex: 'tpname',
      fixed: 'left',
      width: '15%',
      filtrable: true,
    },
    {
      dataIndex: 'tptype',
      fixed: 'left',
      width: '15%',
      filtrable: true,
      filterComponent: <ParListSelect parcode="TPTYPE" />,
      render: (value, record) => {
        if (value === 'PM') {
          return <Text> Company </Text>;
        } else {
          return <Text> Individual </Text>;
        }
      },
    },
    {
      dataIndex: 'parentreference',
      render: (parentreference, record) =>
        !onSelect ? (
          <RouterLink to={`/third-party/${record.parentid}/TP`}>
            {parentreference}
          </RouterLink>
        ) : (
          parentreference
        ),
      fixed: 'left',
      width: '10%',
      filtrable: false,
    },
    {
      comcode: 'parentname',
      dataIndex: 'parentid',
      fixed: 'left',
      width: '15%',
      filtrable: true,
      filterComponent: <ThirdPartyInput onlytpid />,
      render: (tpid, record) => record.parentname,
    },
    {
      dataIndex: 'tagroles',
      width: '10%',

      filtrable: 'role',
      filterComponent: !parcodeRole ? (
        <ParListSelect
          parcode="TPMAINROLE"
          value={initialSearchParams?.role ? initialSearchParams?.role : ''}
          disabled={initialSearchParams?.role === 'CLIENT' && isProposalCreate}
          style={{ width: '100%' }}
        ></ParListSelect>
      ) : (
        <ParListSelect
          parcode={parcodeRole ? parcodeRole : 'TPMAINROLE'}
          disabled={parcodeRole ? false : true}
          style={{ width: '100%' }}
        />
      ),

      render: (tagroles) => (
        <>
          {reject(tagroles.split(','), isEmpty).map((role) => (
            <RoleTag role={role} key={role} />
          ))}
        </>
      ),
    },
    {
      dataIndex: 'tpmainadrstreet1',
      width: '20%',
    },
    {
      dataIndex: 'tpmainadrzipcode',
      width: '20%',
    },
    {
      dataIndex: 'tpmainadrcity',
      width: '15%',
    },
  ];

  return (
    <DF.Provider pagcode="tpsfslist">
      {!search ? (
        <>
          <Card
            title={<Title level={5} comcode="RECHERCHE D’UN TIERS "></Title>}
            bordered={false}
          >
            <Row justify="center">
              <Col span={12}>
                <Form form={form}>
                  <Collapse
                    bordered={false}
                    expandIconPosition="start"
                    defaultActiveKey={['1']}
                  >
                    <Panel
                      header={
                        <Title level={5} comcode="Recherche Simple"></Title>
                      }
                      key="1"
                      style={{
                        marginBottom: 24,
                        background: '#f7f7f7',
                        border: 10,
                        borderRadius: 2,
                      }}
                    >
                      <Row justify="center">
                        <Col xs={24}>
                          <FormInput name="tpreference" className="sfs_style">
                            <Input />
                          </FormInput>
                        </Col>
                        <Col xs={24}>
                          <FormInput name="parentid" className="sfs_style">
                            <ThirdPartyInput onlytpid />
                          </FormInput>
                        </Col>
                        <Col xs={24}>
                          <FormInput name="tpname" className="sfs_style">
                            <Input />
                          </FormInput>
                        </Col>
                      </Row>
                    </Panel>
                    <Panel
                      header={
                        <Title
                          level={5}
                          comcode="Recherche Multicritères"
                        ></Title>
                      }
                      key="2"
                      style={{
                        marginBottom: 24,
                        background: '#f7f7f7',
                        border: 10,
                        borderRadius: 2,
                      }}
                    >
                      <Col xs={24}>
                        <FormInput name="tprole" className="sfs_style">
                          <ParListSelect parcode="TPMAINROLE" />
                        </FormInput>
                      </Col>

                      <Col xs={24}>
                        <FormInput
                          name="tpmainadrzipcode"
                          className="sfs_style"
                        />
                      </Col>

                      <Col xs={24}>
                        <FormInput name="tpmainadrcity" className="sfs_style" />
                      </Col>
                    </Panel>
                  </Collapse>
                </Form>
              </Col>
            </Row>
          </Card>

          <Row justify="center">
            <Col span={4}>
              <SearchButton
                htmlType="submit"
                comcode="search"
                style={{ width: '10%' }}
                className="sfs_style"
                onClick={() => {
                  let val = form.getFieldsValue();

                  setSearchParams({
                    parentid: val?.parentid ?? 0,
                    role: val?.role ?? '',
                    tpfiscalid: val?.tpfiscalid ?? ('' as any),
                    tpname: val?.tpname ?? '',
                    tpreference: val?.tpreference ?? '',
                    tpcardid: val?.tpcardid ?? ('' as any),
                    tptype: val?.tptype ?? '',
                  });

                  setSearch(true);
                }}
                type="primary"
              />
            </Col>
          </Row>
        </>
      ) : (
        <DataGrid
          titleComcode="pageTitleList"
          columns={columns}
          dataSource={thirdParties}
          rowKey="tpid"
          isLoading={isFetching}
          onSearch={(values) => {
            // setSearchParams({
            //   ...values,
            //   tpreference: values.tpreference,
            //   parentid: values.parentid,
            //   tpname: values.tpname,
            //   role: values.tprole,
            // });
            // setsearchs(true);
            // refetch();
          }}
          initialSearchValues={{
            ...initialSearchParams,
          }}
          className="sfs_style"
          componentName="tp_list"
          pagination={{ pageSize: 50 }}
          hideExtraAction
          hideFilters
        />
      )}
    </DF.Provider>
  );
};

export default ThirdPartyListSFS;
